import { useEffect, useState } from 'react';
import { BadgeDollarSign, CheckCircle2, CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import { services } from '../data/content';
import { sendAwaitingPaymentEmail } from '../lib/email';
import { createPendingOrder } from '../lib/orders';
import { startPayment, type PaymentResult } from '../lib/payments';
import type { CheckoutMethod, PaymentStatus, PendingOrder, ServiceModel, ServiceTier } from '../types/portfolio';
import { SectionHeading } from './SectionHeading';

const methods: { id: CheckoutMethod; label: string }[] = [
  { id: 'stripe', label: 'Stripe' },
  { id: 'card', label: 'Card' },
];

type CheckoutProps = {
  selectedTier: ServiceTier;
  pendingOrder: PendingOrder | null;
  onSelectTier: (tier: ServiceTier) => void;
};

function statusTitle(status: PaymentStatus) {
  if (status === 'idle') return 'Ready';
  if (status === 'payment_ready') return 'Payment link ready';
  return status;
}

export function Checkout({ selectedTier, pendingOrder, onSelectTier }: CheckoutProps) {
  const [selectedModel, setSelectedModel] = useState<ServiceModel>(selectedTier.models[0]);
  const [method, setMethod] = useState<CheckoutMethod>('stripe');
  const [status, setStatus] = useState<PaymentStatus>('idle');
  const [result, setResult] = useState<PaymentResult | null>(null);
  const [preparedOrderId, setPreparedOrderId] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    setSelectedModel(selectedTier.models[0]);
    setStatus('idle');
    setResult(null);
    setPreparedOrderId(null);
    setEmailSent(false);
  }, [selectedTier]);

  useEffect(() => {
    setPreparedOrderId(null);
    setResult(null);
    setStatus('idle');
    setEmailSent(false);
  }, [pendingOrder, selectedModel]);

  const handleCheckout = async () => {
    if (!pendingOrder) {
      setStatus('error');
      setResult({
        orderId: 'ORDER-FIRST',
        invoiceId: 'PENDING',
        status: 'error',
        message: 'First, fill out the "Order Work" form, then you can pay.',
      });
      document.querySelector('#order')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setStatus('processing');
    setResult(null);

    try {
      const orderRecord =
        preparedOrderId
          ? { id: preparedOrderId }
          : await createPendingOrder(pendingOrder.values, pendingOrder.files, selectedTier, selectedModel);
      setPreparedOrderId(orderRecord.id);

      if (!emailSent) {
        await sendAwaitingPaymentEmail(orderRecord.id, pendingOrder.values, pendingOrder.files, selectedTier, selectedModel);
        setEmailSent(true);
      }

      const nextResult = await startPayment({
        tier: selectedTier,
        model: selectedModel,
        method,
        orderId: orderRecord.id,
        customerEmail: pendingOrder.values.email,
      });
      setResult(nextResult);
      setStatus(nextResult.status);
    } catch (error) {
      setStatus('error');
      setResult({
        orderId: 'PAY-ERROR',
        invoiceId: 'INV-ERROR',
        status: 'error',
        message: error instanceof Error ? error.message : 'Payment failed.',
      });
    }
  };

  return (
    <section className="section-band" id="checkout">
      <div className="container">
        <SectionHeading eyebrow="Payments" title="Checkout" />

        <div className="checkout-layout reveal">
          <div className="checkout-panel">
            <div className="checkout-title">
              <BadgeDollarSign size={24} />
              <h3>Category</h3>
            </div>

            <div className="segmented">
              {services.map((tier) => (
                <button
                  className={selectedTier.id === tier.id ? 'is-active' : ''}
                  key={tier.id}
                  type="button"
                  onClick={() => onSelectTier(tier)}
                >
                  {tier.name}
                </button>
              ))}
            </div>

            <div className="service-model-grid">
              {selectedTier.models.map((model) => (
                <button
                  className={selectedModel.id === model.id ? 'is-active' : ''}
                  key={model.id}
                  type="button"
                  onClick={() => {
                    setSelectedModel(model);
                    setStatus('idle');
                    setResult(null);
                    setPreparedOrderId(null);
                  }}
                >
                  <span>{model.name}</span>
                  <strong>{model.price}</strong>
                  <small>{model.note}</small>
                </button>
              ))}
            </div>

            <div className="checkout-summary">
              <span>{selectedTier.name}</span>
              <strong>{selectedModel.price}</strong>
              <p>{selectedModel.name} - {selectedModel.note}</p>
              <div className="tech-list model-feature-list">
                {selectedModel.features.map((feature) => (
                  <span key={feature}>{feature}</span>
                ))}
              </div>
            </div>

            <div className="method-grid method-grid-two">
              {methods.map((item) => (
                <button
                  className={method === item.id ? 'is-active' : ''}
                  key={item.id}
                  type="button"
                  onClick={() => setMethod(item.id)}
                >
                  <CreditCard size={18} />
                  {item.label}
                </button>
              ))}
            </div>

            {!pendingOrder ? (
              <div className="form-alert">Fill out the "Order Work" form to activate payment.</div>
            ) : (
              <div className="form-success">
                <CheckCircle2 size={18} />
                Application is ready. Now you can proceed to payment.
              </div>
            )}

            <button className="btn btn-primary submit-button" type="button" onClick={handleCheckout} disabled={status === 'processing'}>
              {status === 'processing' ? <Loader2 className="spin" size={18} /> : <ShieldCheck size={18} />}
              Pay Now
            </button>
          </div>

          <div className={`payment-status status-page-${status}`}>
            <span>Payment status</span>
            <h3>{statusTitle(status)}</h3>
            <p>{result?.message || 'Order ID and invoice will appear after checkout starts.'}</p>
            <div className="invoice-lines">
              <div>
                <span>Model</span>
                <strong>{selectedModel.name}</strong>
              </div>
              <div>
                <span>Order ID</span>
                <strong>{result?.orderId || preparedOrderId || 'Pending'}</strong>
              </div>
              <div>
                <span>Invoice</span>
                <strong>{result?.invoiceId || 'Pending'}</strong>
              </div>
              <div>
                <span>Method</span>
                <strong>{method.toUpperCase()}</strong>
              </div>
            </div>

            <div className="payment-actions">
              {result?.redirectUrl ? (
                <a className="btn btn-ghost" href={result.redirectUrl} target="_blank" rel="noreferrer">
                  Open payment link
                </a>
              ) : null}
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
